// Name column: goal name + goal-type badge/label + dynamic tenure pill.
import { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, useTheme } from '@mui/material';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useIsMultiPortfolioTenant } from '../../../common/hooks';
import { TermChip, TextboxInput, Typography } from '../../../components/atoms';
import { GoalTypeBadge } from '../GoalTypeTiles';
import {
    computeGoalTenure,
    GoalInlineRow,
    GoalRowType,
    isGoalTenureLocked,
    RETIREMENT_TENURE,
    toDayjs
} from '../goalInlineHelpers';
import { goalsReadTextSx } from './shared';
import type { GoalRowCtx } from './shared';

// Same name pattern used by all three Flow2 goal drawers.
// eslint-disable-next-line no-useless-escape
const GOAL_NAME_PATTERN =
    /^(?!\s)(?!.*\s$)([A-Za-z0-9\s’'.\-&/+()áéíóúüñÁÉÍÓÚÜÑàâäèêëîïôöùûçÀÂÄÈÊËÎÏÔÖÙÛÇ])*$/i;

const TYPE_LABEL_KEY: Record<GoalRowType, string> = {
    income: 'INCOME_GOAL_CARD_HEADER',
    wealth: 'WEALTH_GOAL_CARD_HEADER',
    retirement: 'RETIREMENT_GOAL_CARD_HEADER'
};

// True when two row/form dates fall in the same month (treats both-empty as equal); used to
// detect whether a goal's driving dates actually changed while it is open for edit.
const isSameGoalMonth = (a: Parameters<typeof toDayjs>[0], b: Parameters<typeof toDayjs>[0]) => {
    const da = toDayjs(a ?? null);
    const db = toDayjs(b ?? null);
    if (!da.isValid() && !db.isValid()) return true;
    return da.isValid() && db.isValid() && da.isSame(db, 'month');
};

const GoalTypeCaption = ({
    type,
    term,
    loading = false
}: {
    type: GoalRowType;
    term?: string | null;
    loading?: boolean;
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    // AEX-3108: the term pill only applies to multiportfolio tenants.
    const isMultiPortfolioTenant = useIsMultiPortfolioTenant();
    const showAside = isMultiPortfolioTenant && (loading || !!term);
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mt: '8px', flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <GoalTypeBadge type={type} />
                <Typography
                    variant="labels.large"
                    label={t(TYPE_LABEL_KEY[type])}
                    sx={goalsReadTextSx(theme).typeLabel}
                />
            </Box>
            {showAside ? (
                <>
                    <Box
                        sx={{ width: '1px', height: '17px', backgroundColor: theme.palette.grey[200], mx: '2px' }}
                    />
                    {loading ? (
                        <CircularProgress size={16} thickness={5} aria-label={t('SPINNER_LOADING_MESSAGE')} />
                    ) : (
                        <TermChip term={term ?? undefined} variant="bordered" />
                    )}
                </>
            ) : null}
        </Box>
    );
};

export const NameCellRead = ({ row }: { row: GoalInlineRow }) => {
    const theme = useTheme();
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="body.large" label={row.goalName || '-'} sx={goalsReadTextSx(theme).value} />
            <GoalTypeCaption type={row.type} term={row.goalCategory} />
        </Box>
    );
};

export const NameCellEdit = ({
    ctx,
    duplicateNameError,
    onClearDuplicate
}: {
    ctx: GoalRowCtx;
    duplicateNameError?: string;
    onClearDuplicate?: () => void;
}) => {
    const { t } = useTranslation();
    const type = ctx.row.type as GoalRowType;
    const language = useAppSelector((state) => state?.global?.currentLanguage);
    const isTenureLocked = useAppSelector((state) => isGoalTenureLocked(ctx.row, state.adviceGoals));
    const isMultiPortfolioTenant = useIsMultiPortfolioTenant();
    const { setValue } = ctx;

    // Dynamic tenure pill: recompute the bucket whenever the driving dates change. While the
    // generategoalcategory API is in flight we show a spinner in the pill slot and keep the row
    // invalid via the hidden `_tenureLoading` field, which disables the save action.
    const [term, setTerm] = useState<string | null>(ctx.row.goalCategory ?? null);
    const [loading, setLoading] = useState(false);
    const incomeStartDate = ctx.watch('incomeStartDate');
    const targetDate = ctx.watch('targetDate');
    const targetValid = toDayjs(targetDate).isValid();
    const debounceRef = useRef<ReturnType<typeof setTimeout>>();
    // Snapshot the goal's driving dates + known tenure on open, so an unchanged existing goal
    // doesn't re-hit generategoalcategory just for being edited.
    const initialIncomeStartRef = useRef(ctx.row.incomeStartDate ?? null);
    const initialTargetDateRef = useRef(ctx.row.targetDate ?? null);
    const initialGoalCategoryRef = useRef(ctx.row.goalCategory ?? null);

    useEffect(() => {
        const applyLoading = (val: boolean) => {
            setLoading(val);
            setValue('_tenureLoading', val as never, { shouldValidate: true, shouldDirty: false });
        };
        if (!isMultiPortfolioTenant) {
            // No term pill or generategoalcategory call for single-portfolio tenants.
            setTerm(null);
            applyLoading(false);
            return;
        }
        if (type === 'retirement') {
            // Canonical (untranslated) term: TermChip localizes it and keys its colours off it.
            setTerm(RETIREMENT_TENURE.goalCategory);
            applyLoading(false);
            return;
        }
        // AEX: a saved (tenure-locked) goal keeps its saved term; never re-hit generategoalcategory.
        if (isTenureLocked) {
            setTerm(initialGoalCategoryRef.current);
            applyLoading(false);
            return;
        }
        if (!targetValid) {
            setTerm(null);
            applyLoading(false);
            return;
        }
        if (
            initialGoalCategoryRef.current &&
            isSameGoalMonth(incomeStartDate, initialIncomeStartRef.current) &&
            isSameGoalMonth(targetDate, initialTargetDateRef.current)
        ) {
            // Editing an existing goal with unchanged dates: reuse the known tenure.
            setTerm(initialGoalCategoryRef.current);
            applyLoading(false);
            return;
        }
        applyLoading(true);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            const tenure = await computeGoalTenure(
                type,
                { type, incomeStartDate, targetDate } as GoalInlineRow,
                language
            );
            setTerm(tenure.goalCategory);
            applyLoading(false);
        }, 300);
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [type, incomeStartDate, targetDate, targetValid, language, t, setValue, isTenureLocked, isMultiPortfolioTenant]);

    
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <Controller
                name="goalName"
                control={ctx.control}
                rules={{
                    required: t('GOAL_NAME_REQUIRED_ERROR'),
                    maxLength: { value: 50, message: t('GOAL_NAME_MAXLENGTH_ERROR') },
                    pattern: { value: GOAL_NAME_PATTERN, message: t('NAME_INVALID_CHARACTERS_ERROR') }
                }}
                render={({ field }) => (
                    <TextboxInput
                        onBlur={field.onBlur}
                        onChange={(e) => {
                            field.onChange(e);
                            if (duplicateNameError) onClearDuplicate?.();
                        }}
                        value={field.value ?? ''}
                        inputRef={field.ref}
                        size="small"
                        error={!!ctx.errors.goalName || !!duplicateNameError}
                        showTooltipError
                        openTooltipError={
                            (!!ctx.errors.goalName || !!duplicateNameError) &&
                            Object.keys(ctx.errors).length <= 1
                        }
                        helperText={duplicateNameError || (ctx.errors.goalName?.message as string | undefined)}
                        type="text"
                        data-testid="goalName"
                        placeholder={t('GOALS_INLINE_NAME_PLACEHOLDER')}
                        fullWidth
                    />
                )}
            />
            {/* Hidden field: invalid while the tenure API loads so the save (tick) action is disabled. */}
            <Controller
                name={'_tenureLoading' as never}
                control={ctx.control}
                rules={{ validate: (v) => v !== true || 'tenure-loading' }}
                render={() => <></>}
            />
            <GoalTypeCaption type={type} term={term} loading={loading} />
        </Box>
    );
};
